import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, PropType } from 'vue';
import type { FormItem } from '@/lib/formItem';
import type {
  SdsSection3Material,
  SdsSection3ImpurityMaterial,
} from '@/interfaces/model/general/sds';

import {
  getSection15TypeList,
  getLawList,
  getRegulation1List,
  getRegulation2List,
  getRegulation3List,
  getRegulation4List,
} from '@/lib/formItem';
import { required } from '@/lib/rule';
import { RegulationId } from '@/lib/enum';
import { VForm } from 'vuetify/lib/components';
import sharedMessages from '@/lib/shared-messages';
import { defineMessages } from '@/lib/i18n';

import { SdsSection15Material } from '@/interfaces/model/general/sds';
import { SdsSection15Cas } from '@/interfaces/model/general/sds';

type Section3MaterialWithFlag = (
  | SdsSection3Material
  | SdsSection3ImpurityMaterial
) & {
  is_impurity?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsSection15MaterialForm',
  props: {
  isValid: {
    type: Boolean,
    required: true,
  },
  isEditing: {
    type: Boolean,
    required: true,
  },
  lawId: {
    type: Number,
    required: true,
  },
  applicableId: {
    type: Number,
    required: true,
    validator: (value: number) => {
      return [1, 2, 3, 4].includes(value);
    },
  },
  regulation1Id: {
    type: Number,
    required: true,
  },
  regulation2Id: {
    type: Number,
    required: true,
  },
  regulation3Id: {
    type: Number,
    required: true,
  },
  regulation4Id: {
    type: Number,
    required: true,
  },
  regulation5Id: {
    type: Number,
    required: true,
  },
  section3MaterialList: {
    type: Array as PropType<Array<Section3MaterialWithFlag>>,
    required: true,
  },
  materialList: {
    type: Array as PropType<Array<SdsSection15Material>>,
    required: true,
  },
  sdsId: {
    type: Number,
    required: true,
  },
  selectedMaterial: {
    type: Object as PropType<SdsSection15Material | SdsSection3Material | null>,
    default: null,
  },
},
  emits: ["update:isValid", "update:applicableId", "update:lawId", "update:regulation1Id", "update:regulation2Id", "update:regulation3Id", "update:regulation4Id", "update:regulation5Id"],
  setup(__props, { expose, emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:applicableId', data: number): void;
  (e: 'update:lawId', data: number): void;
  (e: 'update:regulation1Id', data: number): void;
  (e: 'update:regulation2Id', data: number): void;
  (e: 'update:regulation3Id', data: number): void;
  (e: 'update:regulation4Id', data: number): void;
  (e: 'update:regulation5Id', data: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const messages = defineMessages({
  manualInputHint:
    '選択肢以外の成分を入力する場合は、入力後にEnterキーを押してください',
});

// Type for Section 3 materials with impurity flag


const form = ref<InstanceType<typeof VForm> | null>(null);

const cas = ref<Array<string>>([]);
const searchLawValue = ref<string>('');
const searchRegulation1Value = ref<string>('');
const searchRegulation2Value = ref<string>('');
const searchRegulation3Value = ref<string>('');
const searchRegulation4Value = ref<string>('');
const selectedSection3Material = ref<string | FormItem | null>(null);



const section3MaterialDropdownItems = computed(() => {
  const materialList = (props.section3MaterialList || []).filter(
    (c) => !c.deleted_at
  );
  if (materialList.length === 0) {
    return [];
  }
  return materialList.map((c) => ({
    text: c.material_name,
    value: c.material_name,
    isImpurity: c.is_impurity || false,
  }));
});

const casFormItems = computed((): Array<FormItem> => {
  if (!selectedSection3Material.value) return [];
  if (typeof selectedSection3Material.value !== 'object') return [];
  const material = props.section3MaterialList.find(
    (c) => c.material_name === selectedSection3Material.value.value
  );

  if (!material) return [];
  return (material.cas_list || []).map((cas) => ({
    text: cas.cas_number,
    value: cas.cas_number,
  }));
});

function onSection3MaterialChange(material: FormItem | string | null) {
  if (material && props.section3MaterialList) {
    if (typeof material === 'object') {
      const foundMaterial: Section3MaterialWithFlag | undefined =
        props.section3MaterialList.find(
          (c) => c.material_name === material.value
        );
      if (foundMaterial) {
        cas.value = foundMaterial.cas_list.map((c) => c.cas_number);
      } else {
        cas.value = [];
      }
    }
  }
}

const createSdsSection15Material = (
  material: Section3MaterialWithFlag | SdsSection15Material
): SdsSection15Material => {
  const normalizedCasList = (cas.value || []).map((casItem) =>
    typeof casItem === 'string' ? casItem : casItem.value || casItem.text || ''
  );

  const material_id = props.selectedMaterial ? props.selectedMaterial.id : 0;
  const newCasList: SdsSection15Cas[] = normalizedCasList.map((casNumber) => ({
    id: 0,
    sds_id: material.sds_id,
    sds_section15_material_id: material_id,
    cas_number: casNumber,
    created_at: '',
    updated_at: '',
    deleted_at: null,
  }));

  return {
    id: material_id,
    sds_id: material.sds_id,
    sds_section15_id: 0,
    material_name: material.material_name,
    cas_list: newCasList,
    created_at: '',
    updated_at: '',
    deleted_at: null,
  };
};

const selectedLawRegulation1List = computed(() => {
  const list = [
    {
      value: RegulationId.DEFAULT.value,
      text: '',
    },
    ...getRegulation1List().filter((item) => {
      return item.lawId === props.lawId;
    }),
  ];

  return list;
});

const selectedLawRegulation2List = computed(() => {
  const list = [
    {
      value: RegulationId.DEFAULT.value,
      text: '',
    },
    ...getRegulation2List().filter((item) => {
      return item.lawId === props.lawId;
    }),
  ];

  return list;
});

const selectedLawRegulation3List = computed(() => {
  const list = [
    {
      value: RegulationId.DEFAULT.value,
      text: '',
    },
    ...getRegulation3List().filter((item) => {
      return item.lawId === props.lawId;
    }),
  ];

  return list;
});

const selectedLawRegulation4List = computed(() => {
  const list = [
    {
      value: RegulationId.DEFAULT.value,
      text: '',
    },
    ...getRegulation4List().filter((item) => {
      return item.lawId === props.lawId;
    }),
  ];

  return list;
});

const updateIsValid = (e: boolean): void => {
  emits('update:isValid', e);
};

const updateApplicableId = (e: number): void => {
  emits('update:applicableId', e);
};

const updateLawId = (e: number): void => {
  emits('update:lawId', e);
};

const updateRegulation1Id = (e: number | null): void => {
  emits('update:regulation1Id', e ?? RegulationId.DEFAULT.value);
};

const updateRegulation2Id = (e: number | null): void => {
  emits('update:regulation2Id', e ?? RegulationId.DEFAULT.value);
};

const updateRegulation3Id = (e: number | null): void => {
  emits('update:regulation3Id', e ?? RegulationId.DEFAULT.value);
};

const updateRegulation4Id = (e: number | null): void => {
  emits('update:regulation4Id', e ?? RegulationId.DEFAULT.value);
};

onMounted((): void => {
  if (form.value) {
    form.value.validate();
  }

  // Initialize with selectedMaterial from props if it exists
  if (props.selectedMaterial && props.selectedMaterial.material_name) {
    // Set the selected material
    selectedSection3Material.value = {
      text: props.selectedMaterial.material_name,
      value: props.selectedMaterial.material_name,
    };

    // Set the CAS numbers
    if (Array.isArray(props.selectedMaterial.cas_list)) {
      cas.value = props.selectedMaterial.cas_list.map((c) => c.cas_number);
    }
  }
});

const syncMaterials = () => {
  const material = selectedSection3Material.value;

  if (!material) {
    return false;
  }

  let materialToInclude: SdsSection15Material | null = null;
  if (material && props.section3MaterialList) {
    if (typeof material === 'object') {
      const foundMaterial: Section3MaterialWithFlag | undefined =
        props.section3MaterialList.find(
          (c) => c.material_name === material.value
        );
      if (foundMaterial) {
        materialToInclude = createSdsSection15Material(foundMaterial);
      }
    } else if (typeof material === 'string') {
      const foundMaterial = {
        id: 0,
        sds_id: String(props.sdsId),
        sds_section15_id: 0,
        material_name: material,
        cas_list: [],
        created_at: '',
        updated_at: '',
        deleted_at: null,
      };
      materialToInclude = createSdsSection15Material(foundMaterial);
    }
  }
  const material_list = materialToInclude ? [materialToInclude] : [];

  emits('update:materialList', material_list);
  return true;
};

expose({
  syncMaterials,
});

return { __sfc: true,messages, props, form, cas, searchLawValue, searchRegulation1Value, searchRegulation2Value, searchRegulation3Value, searchRegulation4Value, selectedSection3Material, emits, section3MaterialDropdownItems, casFormItems, onSection3MaterialChange, createSdsSection15Material, selectedLawRegulation1List, selectedLawRegulation2List, selectedLawRegulation3List, selectedLawRegulation4List, updateIsValid, updateApplicableId, updateLawId, updateRegulation1Id, updateRegulation2Id, updateRegulation3Id, updateRegulation4Id, syncMaterials, getSection15TypeList, getLawList, required, RegulationId, VForm, sharedMessages }
}

})